/* ═══════════════════════════════════════════════════════════════════════
   useDocumentUploads.js
   Uploads attachments the moment they are picked, then follows their
   indexing progress over the session's SSE stream.

   Flow
   ────
   1. addFiles(files)  → one POST per file
        POST /documents/v2/{userid}/upload     (form: files, session_id)
      The response gives each file its document_id + current status.
   2. While any document is still processing, an EventSource listens on
        GET  /documents/v2/{userid}/session/{sessionId}/events
      and every `doc_status` event updates the matching chip.
      The stream is closed once nothing is pending (`idle` or all ready).
   3. On send, the page reads `documentIds` and posts them to /completion
      as `document_ids` (JSON array) instead of the raw files.
   ═══════════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import config from "../config/pyconfig";

const READY = new Set(["ready"]);
const FAILED = new Set(["failed", "error", "rejected"]);

export const isDocReady = (d) => READY.has(d.status);
export const isDocFailed = (d) => FAILED.has(d.status);
/** Still uploading (no id yet) or being indexed on the server. */
export const isDocPending = (d) => !isDocReady(d) && !isDocFailed(d);

let localSeq = 0;
const nextKey = () => `att-${Date.now()}-${localSeq++}`;

/* Copies the server's fields onto a chip without losing local ones. */
const mergeServerDoc = (doc, server = {}) => ({
  ...doc,
  documentId: server.document_id || doc.documentId,
  status: server.status || doc.status,
  pct: typeof server.pct === "number" ? server.pct : doc.pct,
  message: server.message || doc.message,
  pageCount: server.page_count ?? doc.pageCount,
  error: server.error || null,
});

export default function useDocumentUploads({ userid, sessionId, maxFiles = 5, onError }) {
  const [docs, setDocs] = useState([]);
  const docsRef = useRef(docs);
  const sourceRef = useRef(null);
  const sessionRef = useRef(sessionId);
  const onErrorRef = useRef(onError);

  useEffect(() => { docsRef.current = docs; }, [docs]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const patch = useCallback((key, updater) => {
    setDocs((prev) => prev.map((d) => (d.key === key ? updater(d) : d)));
  }, []);

  /* ── SSE: live status for documents still being indexed ─────────────── */
  const closeStream = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  const openStream = useCallback(() => {
    const sid = sessionRef.current;
    if (sourceRef.current || !userid || !sid) return;

    const es = new EventSource(
      `${config.apiBaseUrl}/documents/v2/${userid}/session/${sid}/events`
    );
    sourceRef.current = es;

    es.addEventListener("doc_status", (e) => {
      let data;
      try { data = JSON.parse(e.data); } catch (_) { return; }
      if (!data?.document_id) return;

      // The stream reports every document in the session; only touch ours.
      setDocs((prev) =>
        prev.some((d) => d.documentId === data.document_id)
          ? prev.map((d) => (d.documentId === data.document_id ? mergeServerDoc(d, data) : d))
          : prev
      );
    });

    es.addEventListener("idle", () => {
      if (!docsRef.current.some(isDocPending)) closeStream();
    });

    // EventSource reconnects on its own (server sends `retry: 3000`);
    // only stop if there is nothing left to wait for.
    es.onerror = () => {
      if (!docsRef.current.some(isDocPending)) closeStream();
    };
  }, [userid, closeStream]);

  // Open while something is processing on the server, close when done.
  useEffect(() => {
    const waiting = docs.some((d) => d.documentId && isDocPending(d));
    if (waiting) openStream();
    else if (!docs.some(isDocPending)) closeStream();
  }, [docs, openStream, closeStream]);

  /* ── Upload ─────────────────────────────────────────────────────────── */
  const uploadOne = useCallback(
    async (doc) => {
      const sid = sessionRef.current;
      if (!userid || !sid) {
        patch(doc.key, (d) => ({ ...d, status: "failed", message: "No active session", error: "No session" }));
        return;
      }

      const form = new FormData();
      form.append("files", doc.file);
      form.append("session_id", sid);

      try {
        const res = await fetch(`${config.apiBaseUrl}/documents/v2/${userid}/upload`, {
          method: "POST",
          body: form,
        });
        const data = await res.json().catch(() => ({}));

        // Session switched while this was in flight — drop the result.
        if (sessionRef.current !== sid) return;

        const server = data?.documents?.[0];
        if (!res.ok || !server) {
          const rejected = data?.rejected?.[0];
          const reason =
            rejected?.reason || rejected?.error || rejected?.message ||
            data?.detail || data?.message || "Upload failed";
          patch(doc.key, (d) => ({ ...d, status: "failed", message: String(reason), error: String(reason) }));
          onErrorRef.current?.(`${doc.name}: ${reason}`);
          return;
        }

        patch(doc.key, (d) => mergeServerDoc(d, server));
      } catch (err) {
        if (sessionRef.current !== sid) return;
        patch(doc.key, (d) => ({ ...d, status: "failed", message: "Upload failed", error: err?.message }));
        onErrorRef.current?.(`${doc.name}: upload failed`);
      }
    },
    [userid, patch]
  );

  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList || []);
      if (!incoming.length) return;

      const slots = maxFiles - docsRef.current.length;
      if (slots <= 0 || incoming.length > slots) {
        onErrorRef.current?.(`You can upload a maximum of ${maxFiles} files.`);
      }
      const accepted = incoming.slice(0, Math.max(0, slots));
      if (!accepted.length) return;

      const fresh = accepted.map((file) => ({
        key: nextKey(),
        file,
        name: file.name,
        mimeType: file.type,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        documentId: null,
        status: "uploading",
        pct: 0,
        message: "Uploading…",
        error: null,
      }));

      setDocs((prev) => [...prev, ...fresh]);
      fresh.forEach(uploadOne);
    },
    [maxFiles, uploadOne]
  );

  const removeDoc = useCallback((key) => {
    setDocs((prev) => {
      const gone = prev.find((d) => d.key === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((d) => d.key !== key);
    });
  }, []);

  const clear = useCallback(() => {
    docsRef.current.forEach((d) => d.previewUrl && URL.revokeObjectURL(d.previewUrl));
    docsRef.current = [];
    setDocs([]);
    closeStream();
  }, [closeStream]);

  // New / different chat → forget the previous chat's attachments.
  useEffect(() => {
    if (sessionRef.current !== sessionId) {
      sessionRef.current = sessionId;
      clear();
    }
  }, [sessionId, clear]);

  useEffect(() => () => closeStream(), [closeStream]);

  /** Ids to send with the next message (uploaded and not failed). */
  const documentIds = useMemo(
    () => docs.filter((d) => d.documentId && !isDocFailed(d)).map((d) => d.documentId),
    [docs]
  );

  /** True while a file is still on its way up and has no id yet. */
  const isUploading = useMemo(() => docs.some((d) => d.status === "uploading"), [docs]);

  /** True until every attachment is ready (or failed) — Send waits on this. */
  const hasPending = useMemo(() => docs.some(isDocPending), [docs]);

  return { docs, documentIds, isUploading, hasPending, addFiles, removeDoc, clear };
}